import _ from 'lodash';
import { z } from 'zod';
import * as schemas from '../schemas';
import getObjId from './getObjId';

export interface SanitizeModelOptions {
  /** Model name in camelCase, e.g. 'tournament', 'divisionTeam', 'game', 'stat'. */
  model: string;
  /** For sport-polymorphic models (`game`, `stat`); defaults to `'basketball'`. Ignored otherwise. */
  sport?: string;
}

const POLYMORPHIC_MODELS = new Set(['game', 'stat']);
const DEFAULT_SPORT = 'basketball';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function resolveSchemaName({ model, sport }: SanitizeModelOptions): string {
  if (POLYMORPHIC_MODELS.has(model)) {
    return `${sport || DEFAULT_SPORT}${cap(model)}`; // e.g. 'basketballGame'
  }
  return model;
}

/**
 * Sanitizes a payload for sending to the API by:
 *   1. Picking only fields declared on the named model's Zod schema (drops
 *      Mongoose-added fields like `__v`, populated tree data, etc.).
 *   2. Unwrapping populated ref objects to bare ObjectId strings via `getObjId`
 *      (populated refs sent as-is cause Mongoose `Cannot create Buffer` errors).
 *
 * Resolves `<Pascal>Schema` and `<camel>Metadata` from `packages/shared/schemas/`.
 * For `game` and `stat`, the schema is sport-polymorphic — `sport` defaults to
 * `'basketball'` and is used to resolve e.g. `BasketballGameSchema` /
 * `basketballGameMetadata`.
 *
 * Examples:
 *   sanitizeModelPayload({ model: 'tournament' }, t)
 *   sanitizeModelPayload({ model: 'divisionTeam' }, dt)
 *   sanitizeModelPayload({ model: 'game', sport: 'judo' }, g)
 *   sanitizeModelPayload({ model: 'stat' }, s) // basketball
 *
 * Limitation: only top-level refs are unwrapped. Refs nested inside sub-objects
 * (e.g. anything under `entity.config.*`) must be flattened by the caller.
 */
export default function sanitizeModelPayload<T extends Record<string, any>>(
  options: SanitizeModelOptions,
  payload: T,
): Partial<T> {
  const resolved = resolveSchemaName(options);
  const schemaKey = `${cap(resolved)}Schema`;
  const metadataKey = `${resolved}Metadata`;
  const schema = (schemas as any)[schemaKey] as z.ZodObject<any> | undefined;
  const metadata = (schemas as any)[metadataKey] as
    | Record<string, { ref?: string }>
    | undefined;

  if (!schema || typeof (schema as any).shape !== 'object') {
    throw new Error(`sanitizeModelPayload: no "${schemaKey}" exported from @staty/shared/schemas`);
  }
  if (!metadata) {
    throw new Error(`sanitizeModelPayload: no "${metadataKey}" exported from @staty/shared/schemas`);
  }

  const out: Record<string, any> = _.pick(payload, Object.keys(schema.shape));
  for (const [key, meta] of Object.entries(metadata)) {
    if (meta?.ref && out[key] !== undefined) {
      out[key] = Array.isArray(out[key])
        ? out[key].map((v: any) => getObjId(v))
        : getObjId(out[key]);
    }
  }
  return out as Partial<T>;
}
